import React, { useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { C } from "../reporting/reportingStyles";
import { QUILL_MODULES, QUILL_FORMATS, htmlIsEmpty } from "../../constants/merchantPages";
import { inputStyle, labelStyle, hintStyle, errorText, chip } from "./pageStyles";

const filledText = (v) => Boolean(String(v || "").trim());

/**
 * One `{ en, ar }` value with an English / Arabic tab. Only the active language is mounted, which
 * keeps a long page with dozens of rich-text fields light.
 *
 * kind: "text" | "textarea" | "rich"
 */
const BilingualField = ({
  label,
  hint,
  value,
  onChange,
  kind = "text",
  maxLength,
  disabled = false,
  requiredEn = false,
  placeholder,
  testId,
}) => {
  const [lang, setLang] = useState("en");
  const pair = value || { en: null, ar: null };
  const isFilled = (v) => (kind === "rich" ? !htmlIsEmpty(v) : filledText(v));
  const set = (next) => onChange({ ...pair, [lang]: next === "" ? null : next });

  const dir = lang === "ar" ? "rtl" : "ltr";
  const current = pair[lang] || "";
  const missingEn = requiredEn && !isFilled(pair.en);

  let control;
  if (kind === "rich") {
    control = (
      <div dir={dir} data-testid={testId ? `${testId}-${lang}` : undefined}>
        <ReactQuill
          key={lang}
          theme="snow"
          value={current}
          readOnly={disabled}
          // Quill also fires onChange when it normalises the value it was given; only a person
          // typing counts, or merely opening a page would mark it unsaved.
          onChange={(html, delta, source) => { if (source === "user" || source === undefined) set(html); }}
          modules={disabled ? { toolbar: false } : QUILL_MODULES}
          formats={QUILL_FORMATS}
          style={{ background: C.white }}
        />
      </div>
    );
  } else {
    const Tag = kind === "textarea" ? "textarea" : "input";
    control = (
      <Tag
        dir={dir}
        data-testid={testId ? `${testId}-${lang}` : undefined}
        style={{ ...inputStyle, ...(kind === "textarea" ? { minHeight: 70, resize: "vertical" } : {}) }}
        value={current}
        maxLength={maxLength}
        disabled={disabled}
        placeholder={lang === "ar" ? "اختياري — يُعرض الإنجليزي إذا تُرك فارغًا" : placeholder}
        onChange={(e) => set(e.target.value)}
      />
    );
  }

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
        {label && <span style={{ ...labelStyle, marginBottom: 0, flex: 1 }}>{label}</span>}
        {["en", "ar"].map((l) => (
          <button key={l} type="button" style={chip(lang === l)} onClick={() => setLang(l)} aria-pressed={lang === l}>
            {l === "en" ? "English" : "العربية"}
            {isFilled(pair[l]) ? " ●" : ""}
          </button>
        ))}
      </div>
      {control}
      {maxLength && kind !== "rich" && (
        <div style={{ ...hintStyle, textAlign: "right" }}>{current.length}/{maxLength}</div>
      )}
      {missingEn && <div style={errorText}>English is required.</div>}
      {hint && <div style={hintStyle}>{hint}</div>}
    </div>
  );
};

export default BilingualField;
